// src/components/GeneratePhotoshootAnyshootModal.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  ChevronDown,
  X,
  AlertCircle,
  Loader2,
  Brush,
  Eraser,
  Plus,
  Trash2,
} from "lucide-react";
import Loader from "./Loader"; // ✅ same loader as avatar modal

const PAGE_SIZE = 20;

/** Resolve the backend base URL robustly (same pattern as other pages/components) */
const API_BASE =
  process.env.REACT_APP_BACKEND_URL || // legacy
  process.env.REACT_APP_API_URL || // current
  (typeof window !== "undefined" && window._API_BASE) ||
  "http://localhost:4000";

  const BRAND_PINK = "#FC2783";
  const PRODUCT_ANYSHOOT_STYLE = "Lite";

/* ---------------- Module-level caches (per session) ---------------- */

// Cache project assets per projectId
const projectAssetsCache = {}; // { [projectId]: array }

// Cache categories (they rarely change)
const categoriesCache = {
  list: null,
};

// Cache templates per category key
// key = selectedCategory || "all"
const templatesCacheGlobal = {}; // { [key]: { templates, hasMore, page } }

const getTemplateCacheKey = (categoryId) => categoryId || "all";

/* ======================= Category Dropdown ======================= */
function CategoryDropdown({ options, value, onChange, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const optionElements = useMemo(
    () =>
      options.map((opt) => (
        <div
          key={opt.value}
          className={`flex items-center px-3 py-2 cursor-pointer text-sm hover:bg-[#2e2e45] transition select-none ${
            value === opt.value ? "bg-pink-600/10 text-pink-300" : ""
          }`}
          onClick={() => {
            onChange(opt.value);
            setOpen(false);
          }}
        >
          {opt.label}
        </div>
      )),
    [options, value, onChange]
  );

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label || "All Categories",
    [options, value]
  );

  return (
    <div className="relative w-full sm:w-64" ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">{label}</label>
      <button
        type="button"
        className="flex w-full min-h-[38px] items-center px-3 py-1.5 rounded-lg bg-[#23233a] text-white border
          transition-all outline-none focus:ring-2 focus:ring-pink-600 border-[#353550]"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex-1 text-left truncate">{selectedLabel}</span>
        <ChevronDown
          className={`ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          size={18}
        />
      </button>
      {open && (
        <div
          className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-56 overflow-auto animate-fadeIn"
          role="listbox"
        >
          <div
            className={`flex items-center px-3 py-2 cursor-pointer text-sm hover:bg-[#2e2e45] transition select-none ${
              !value ? "bg-pink-600/10 text-pink-300" : ""
            }`}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            role="option"
            aria-selected={!value}
          >
            All Categories
          </div>
          {optionElements}
        </div>
      )}
    </div>
  );
}

/* ======================= Template Card ======================= */
const TemplateCard = React.memo(function TemplateCard({
  template,
  isSelected,
  onSelect,
  templateImageCache,
}) {
  const [imgSrc, setImgSrc] = useState(
    template.coverUrl || template.templateImageUrl
  );

  useEffect(() => {
    const url = template.coverUrl || template.templateImageUrl;
    if (!url) return;

    if (templateImageCache.current[url]) {
      setImgSrc(templateImageCache.current[url]);
    } else {
      const img = new window.Image();
      img.src = url;
      img.onload = () => {
        templateImageCache.current[url] = url;
        setImgSrc(url);
      };
    }
  }, [template, templateImageCache]);

  const promptText =
    template.prompt ||
    template.templatePrompt ||
    template.textPrompt ||
    template.positivePrompt ||
    "";

  return (
    <div
      className={`relative rounded-lg overflow-hidden border-2 group transition cursor-pointer
        ${
          isSelected
            ? "border-pink-500 ring-2 ring-pink-600"
            : "border-transparent hover:border-pink-400"
        }`}
      aria-label="Select template"
      onClick={() => onSelect(String(template.templateId))}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onSelect(String(template.templateId));
          e.preventDefault();
        }
      }}
      role="button"
      title={promptText || template.templateName || "Template"}
    >
      <div
        className="aspect-[4/5] w-full h-full bg-black"
        style={{ minHeight: 140 }}
      >
        <img
          src={imgSrc}
          alt={template.templateName || "Template"}
          className="w-full h-full object-cover block"
          loading="lazy"
          draggable={false}
        />
      </div>

      {(template.templateName || promptText) && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[11px] px-2 py-1 z-10">
          {template.templateName ? (
            <div className="font-semibold truncate">{template.templateName}</div>
          ) : null}
          {promptText ? <div className="opacity-90 truncate">{promptText}</div> : null}
        </div>
      )}
    </div>
  );
});

/* ======================= Modal ======================= */
export default function GeneratePhotoshootAnyshootModal({
  projectId,
  onClose,
  excludeTemplateIds = [],
  onGenerate,
}) {
  const { getToken } = useAuth();
  const [step, setStep] = useState(1);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  // Step 1 assets
  const [projectAssets, setProjectAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsError, setAssetsError] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);

  // ✅ Step 1 manual upload (EXACT Ad Marketing Video flow)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url, ... } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);

  // Step 2 templates
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatePage, setTemplatePage] = useState(1);
  const [hasMoreTemplates, setHasMoreTemplates] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");

  const templatesCache = useRef(new Map());
  const assetCache = useRef({});
  const templateImageCache = useRef({});

  const isSupportedAsset = useCallback((asset) => {
    const url = asset.url || asset.assetUrl || "";
    return /\.(jpe?g|png)$/i.test(url);
  }, []);

  /* ---------- Step 1 assets: match Ad Marketing Video modal layout ---------- */
  const filteredAssets = useMemo(
    () => projectAssets.filter(isSupportedAsset),
    [projectAssets, isSupportedAsset]
  );

  const isAssetsInitialLoading =
    assetsLoading && !assetsError && filteredAssets.length === 0;

  const isAssetsReloadLoading =
    assetsLoading && !assetsError && filteredAssets.length > 0;

  /* ---------- loader flags ---------- */
  const isTemplatesInitialLoading =
    step === 2 && templatesLoading && !templatesError && templates.length === 0;

  /* ---------------- Fetch assets & categories on open ---------------- */
  useEffect(() => {
    let mounted = true;
    setAssetsError("");

    async function fetchAll() {
      try {
        const headers = await authHeaders();

        // --- Assets from project (with cache) ---
        setAssetsLoading(true);
        try {
          let imgs = null;

          if (projectAssetsCache[projectId]) {
            imgs = projectAssetsCache[projectId];
          } else {
            const assetRes = await axios.get(
              `${API_BASE}/api/projects/${projectId}`,
              { headers }
            );
            const rawImgs = (assetRes.data?.images || [])
              .filter((u) => typeof u === "string" && u)
              .map((u, i) => ({
                id: `img-${i}`,
                assetId: `img-${i}`,
                type: "image",
                url: u,
                name: `image-${i}`,
              }))
              .filter(isSupportedAsset);

            imgs = rawImgs;
            projectAssetsCache[projectId] = imgs;
          }

          if (mounted && imgs) {
            setProjectAssets(imgs);

            // warm image cache
            imgs.forEach((asset) => {
              const url = asset.url || asset.assetUrl;
              if (url && !assetCache.current[url]) {
                const img = new window.Image();
                img.src = url;
                img.onload = () => {
                  assetCache.current[url] = url;
                };
              }
            });
          }
        } catch (e) {
          if (mounted) {
            setAssetsError("Failed to load product images.");
            setProjectAssets([]);
          }
        } finally {
          if (mounted) {
            setAssetsLoading(false);
          }
        }

        // --- Categories (with cache) ---
        try {
          let cats = null;

          if (categoriesCache.list) {
            cats = categoriesCache.list;
          } else {
            const catRes = await axios.get(
              `${API_BASE}/api/product-anyshoot/categories`,
              { headers }
            );
            cats = (catRes.data?.result || []).map((c) => ({
              value: String(c.categoryId),
              label: c.categoryName,
            }));
            categoriesCache.list = cats;
          }

          if (mounted && cats) {
            setCategories(cats);
          }
        } catch {
          if (mounted) setCategories([]);
        }
      } catch (e) {
        if (mounted) {
          setAssetsError("Failed to load product images or categories.");
          setCategories([]);
          setProjectAssets([]);
          setAssetsLoading(false);
        }
      }
    }

    if (projectId) fetchAll();
    return () => {
      mounted = false;
    };
  }, [projectId, isSupportedAsset, authHeaders]);

  /* ---------------- Reset when project changes ---------------- */
  useEffect(() => {
    setStep(1);
    setSelectedAsset(null);
    setStep1UploadedAsset(null);
    setStep1UploadLoading(false);

    setTemplates([]);
    setTemplatePage(1);
    setHasMoreTemplates(true);
    setSelectedTemplateId(null);
    setSelectedCategory("");
    setTemplatesLoading(false);
    setTemplatesError("");
    templatesCache.current.clear();
  }, [projectId]);

  /* ---------------- Step 2: Fetch templates (cached) ---------------- */
  useEffect(() => {
    if (step !== 2) return;

    async function fetchTemplatesCached(categoryIdRaw) {
      setTemplatesError("");
      setTemplatesLoading(true);
      setHasMoreTemplates(true);
      setTemplatePage(1);
      setSelectedTemplateId(null);

      const cacheKey = getTemplateCacheKey(categoryIdRaw);

      // ✅ Try global cache first
      if (templatesCacheGlobal[cacheKey]) {
        const cached = templatesCacheGlobal[cacheKey];
        const cachedTemplates = cached.templates || [];
        templatesCache.current.set(cacheKey, cachedTemplates);
        setTemplates(cachedTemplates);
        setHasMoreTemplates(!!cached.hasMore);
        setTemplatePage(cached.page || 1);
        setTemplatesLoading(false);
        return;
      }

      // ✅ Fallback to per-modal cache
      if (templatesCache.current.has(cacheKey)) {
        const cachedTemplates = templatesCache.current.get(cacheKey) || [];
        setTemplates(cachedTemplates);
        setHasMoreTemplates(cachedTemplates.length === PAGE_SIZE);
        setTemplatePage(1);
        setTemplatesLoading(false);
        return;
      }

      // No cache: fetch
      try {
        const headers = await authHeaders();
        const params = {
          style: PRODUCT_ANYSHOOT_STYLE,
          pageNo: 1,
          pageSize: PAGE_SIZE,
          categoryIds: categoryIdRaw || "",
        };
        const res = await axios.get(`${API_BASE}/api/product-anyshoot/templates`, {
          params,
          headers,
        });
        const data = (res.data?.result?.data || []).map((t) => ({
          ...t,
          coverUrl: t.coverUrl,
          templateImageUrl: t.templateImageUrl,
          templateId: String(t.templateId),
        }));

        const hasMore = data.length === PAGE_SIZE;

        templatesCache.current.set(cacheKey, data);
        templatesCacheGlobal[cacheKey] = {
          templates: data,
          hasMore,
          page: 1,
        };

        setTemplates(data);
        setHasMoreTemplates(hasMore);
        setTemplatePage(1);
      } catch {
        setTemplatesError("Failed to load templates.");
        setHasMoreTemplates(false);
      } finally {
        setTemplatesLoading(false);
      }
    }

    fetchTemplatesCached(selectedCategory);
  }, [step, selectedCategory, authHeaders]);

  /* ---------------- Infinite scroll: load more templates -------------- */
  const fetchMoreTemplates = useCallback(async () => {
    const nextPage = templatePage + 1;
    const cacheKey = getTemplateCacheKey(selectedCategory);

    setTemplatesLoading(true);
    setTemplatesError("");
    try {
      const headers = await authHeaders();
      const params = {
        style: PRODUCT_ANYSHOOT_STYLE,
        pageNo: nextPage,
        pageSize: PAGE_SIZE,
        categoryIds: selectedCategory || "",
      };
      const res = await axios.get(`${API_BASE}/api/product-anyshoot/templates`, {
        params,
        headers,
      });
      const data = (res.data?.result?.data || []).map((t) => ({
        ...t,
        coverUrl: t.coverUrl,
        templateImageUrl: t.templateImageUrl,
        templateId: String(t.templateId),
      }));

      const hasMore = data.length === PAGE_SIZE;

      setTemplates((prev) => {
        const merged = [...prev, ...data];
        templatesCache.current.set(cacheKey, merged);
        templatesCacheGlobal[cacheKey] = {
          templates: merged,
          hasMore,
          page: nextPage,
        };
        return merged;
      });

      setHasMoreTemplates(hasMore);
      setTemplatePage(nextPage);
    } catch {
      setTemplatesError("Failed to load more templates.");
      setHasMoreTemplates(false);
    } finally {
      setTemplatesLoading(false);
    }
  }, [templatePage, selectedCategory, authHeaders]);

  /* ---------------- Step 1 manual upload (EXACT behavior from Ad Marketing Video) ---------------- */
  const handleStep1Upload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStep1UploadLoading(true);
      setAssetsError("");

      try {
        const lowerName = String(file.name || "").toLowerCase();
        const isOkType =
          /\.(jpe?g|png)$/i.test(lowerName) ||
          file.type === "image/jpeg" ||
          file.type === "image/png";

        if (!isOkType) {
          setAssetsError("Only JPG or PNG images are supported.");
          return;
        }

        if (file.size > 20 * 1024 * 1024) {
          setAssetsError("Image is too large. Please upload a file under 20MB.");
          return;
        }

        const token = await getToken();
        const formData = new FormData();
        formData.append("file", file);

        // Reuse the same upload endpoint used in Ad Marketing Video
        const { data } = await axios.post(
          `${API_BASE}/api/videos/marketing/endcard/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const url = data?.url;
        if (url) {
          const id = `asset-upload-${Date.now()}`;
          const newAsset = {
            id,
            assetId: id,
            type: "image",
            url,
            name: "uploaded",
          };

          setStep1UploadedAsset(newAsset);
          setSelectedAsset(newAsset);

          assetCache.current[url] = url;
        }
      } catch {
        setAssetsError("Failed to upload image.");
      } finally {
        setStep1UploadLoading(false);
        // reset input so selecting the same file again triggers onChange
        // eslint-disable-next-line no-param-reassign
        event.target.value = "";
      }
    },
    [getToken]
  );

  /* ---------------- Painting logic (Step 3) – TEMPLATE ONLY ----------- */

  const templateCanvasRef = useRef(null);
  const templateImgRef = useRef(null);
  const [templateNatural, setTemplateNatural] = useState({ w: 0, h: 0 });
  const [templateHasPaint, setTemplateHasPaint] = useState(false);

  const [brushSize, setBrushSize] = useState(28);
  const [eraseMode, setEraseMode] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });
  const [expandPx, setExpandPx] = useState(4);
  const [featherPx, setFeatherPx] = useState(1);

  const [generating, setGenerating] = useState(false);

  const selectedAssetUrl = selectedAsset
    ? selectedAsset.url || selectedAsset.assetUrl
    : null;

  const selectedTemplate = templates.find(
    (t) => String(t.templateId) === String(selectedTemplateId)
  );

  const selectedTemplateUrl = selectedTemplate
    ? selectedTemplate.coverUrl || selectedTemplate.templateImageUrl
    : null;

  const selectedTemplatePrompt =
    (selectedTemplate &&
      (selectedTemplate.prompt ||
        selectedTemplate.templatePrompt ||
        selectedTemplate.textPrompt ||
        selectedTemplate.positivePrompt)) ||
    "";

  // Align canvas size EXACTLY with the rendered template image
  const handleTemplateImageLoaded = useCallback(() => {
    if (step !== 3) return;
    const imgEl = templateImgRef.current;
    const canvas = templateCanvasRef.current;
    if (!imgEl || !canvas) return;

    const natW = imgEl.naturalWidth || imgEl.width;
    const natH = imgEl.naturalHeight || imgEl.height;
    setTemplateNatural({ w: natW, h: natH });

    const maxW = 920;
    const maxH = 900;

    let w = natW;
    let h = natH;

    const scale = Math.min(maxW / w, maxH / h);
    w = Math.max(1, Math.round(w * scale));
    h = Math.max(1, Math.round(h * scale));

    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    imgEl.style.width = `${w}px`;
    imgEl.style.height = `${h}px`;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    setTemplateHasPaint(false);
  }, [step]);

  const handleTemplatePointer = (e, type) => {
    if (step !== 3) return;
    const canvas = templateCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const getXY = (ev) => {
      const cx = (ev.clientX ?? ev.touches?.[0]?.clientX) - rect.left;
      const cy = (ev.clientY ?? ev.touches?.[0]?.clientY) - rect.top;
      return [cx, cy];
    };
    const ctx = canvas.getContext("2d");
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = brushSize;

    if (type === "enter") {
      setCursor((c) => ({ ...c, visible: true }));
    } else if (type === "leave") {
      setCursor((c) => ({ ...c, visible: false }));
      setIsDrawing(false);
    } else if (type === "touchstart" || type === "touchmove") {
      setCursor((c) => ({ ...c, visible: false }));
    } else if (type === "touchend") {
      setIsDrawing(false);
    }

    if (type === "down") {
      setIsDrawing(true);
      const [x, y] = getXY(e);
      setCursor({ x, y, visible: true });
      ctx.globalCompositeOperation = eraseMode
        ? "destination-out"
        : "source-over";
      ctx.strokeStyle = eraseMode ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)";
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 0.01, y + 0.01);
      ctx.stroke();
      setTemplateHasPaint(true);
    } else if (type === "move") {
      const [x, y] = getXY(e);
      setCursor({ x, y, visible: true });
      if (isDrawing) {
        ctx.globalCompositeOperation = eraseMode
          ? "destination-out"
          : "source-over";
        ctx.strokeStyle = eraseMode ? "rgba(0,0,0,1)" : "rgba(255,255,255,1)";
        ctx.lineTo(x, y);
        ctx.stroke();
        setTemplateHasPaint(true);
      }
    } else if (type === "up") {
      setIsDrawing(false);
    }
  };

  const clearTemplateMask = () => {
    const canvas = templateCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setTemplateHasPaint(false);
  };

  const buildMaskDataUrl = useCallback(
    (canvasRef, natural, hasPaint) => {
      const viewCanvas = canvasRef.current;
      if (!viewCanvas || !hasPaint) return "";

      const targetW = natural.w || viewCanvas.width;
      const targetH = natural.h || viewCanvas.height;

      const up = document.createElement("canvas");
      up.width = Math.max(1, targetW);
      up.height = Math.max(1, targetH);
      const uctx = up.getContext("2d");
      uctx.clearRect(0, 0, up.width, up.height);
      uctx.imageSmoothingEnabled = true;
      uctx.drawImage(
        viewCanvas,
        0,
        0,
        viewCanvas.width,
        viewCanvas.height,
        0,
        0,
        up.width,
        up.height
      );

      const getImageData = (c) =>
        c.getContext("2d").getImageData(0, 0, c.width, c.height);
      const putImageData = (c, id) =>
        c.getContext("2d").putImageData(id, 0, 0);

      if (featherPx > 0) {
        const tmp = document.createElement("canvas");
        tmp.width = up.width;
        tmp.height = up.height;
        const tctx = tmp.getContext("2d");
        tctx.drawImage(up, 0, 0);
        const passes = Math.min(3, featherPx);
        for (let p = 0; p < passes; p++) {
          for (let dx = -1; dx <= 1; dx += 1)
            if (dx !== 0) tctx.drawImage(tmp, dx, 0);
          for (let dy = -1; dy <= 1; dy += 1)
            if (dy !== 0) tctx.drawImage(tmp, 0, dy);
        }
        uctx.clearRect(0, 0, up.width, up.height);
        uctx.drawImage(tmp, 0, 0);
      }

      let id = getImageData(up);
      const d = id.data;
      for (let i = 0; i < d.length; i += 4) {
        const a = d[i + 3];
        if (a > 16) {
          d[i] = 255;
          d[i + 1] = 255;
          d[i + 2] = 255;
          d[i + 3] = 255;
        } else {
          d[i] = 0;
          d[i + 1] = 0;
          d[i + 2] = 0;
          d[i + 3] = 0;
        }
      }
      putImageData(up, id);

      if (expandPx > 0) {
        const src = getImageData(up);
        const out = new ImageData(up.width, up.height);
        out.data.set(src.data);
        const sw = up.width;
        const sh = up.height;
        const r = Math.max(1, Math.min(12, expandPx));

        for (let y = 0; y < sh; y += 1) {
          for (let x = 0; x < sw; x += 1) {
            const idx = (y * sw + x) * 4;
            if (src.data[idx + 3] === 255) {
              const x0 = Math.max(0, x - r);
              const x1 = Math.min(sw - 1, x + r);
              const y0 = Math.max(0, y - r);
              const y1 = Math.min(sh - 1, y + r);
              for (let yy = y0; yy <= y1; yy += 1) {
                let base = (yy * sw + x0) * 4;
                for (let xx = x0; xx <= x1; xx += 1, base += 4) {
                  out.data[base] = 255;
                  out.data[base + 1] = 255;
                  out.data[base + 3] = 255;
                }
              }
            }
          }
        }
        putImageData(up, out);
      }

      return up.toDataURL("image/png");
    },
    [expandPx, featherPx]
  );

  const ringStyle = {
    position: "absolute",
    left: cursor.x,
    top: cursor.y,
    width: brushSize,
    height: brushSize,
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    borderRadius: "9999px",
    border: `2px solid ${BRAND_PINK}`,
    backgroundColor: "rgba(252,39,131,0.08)",
    boxShadow:
      "0 0 0 1px rgba(252,39,131,0.35), 0 0 12px rgba(252,39,131,0.2)",
    zIndex: 5,
  };

  const canNextFrom1 = !!selectedAsset;
  const canNextFrom2 = !!selectedTemplateId;

  const canGenerate =
    !!selectedAssetUrl && !!selectedTemplateUrl && templateHasPaint;

  /* ---------------- Generate submit ---------------- */
  const handleGenerate = useCallback(async () => {
    if (!onGenerate) return;
    if (!canGenerate) return;

    const imageMaskDataUrl = ""; // optional, unused for now

    const templateMaskDataUrl = buildMaskDataUrl(
      templateCanvasRef,
      templateNatural,
      templateHasPaint
    );

    if (!templateMaskDataUrl) {
      // eslint-disable-next-line no-alert
      alert(
        "Template mask looks empty. Please paint on the template before generating."
      );
      return;
    }

    setGenerating(true);
    try {
      await onGenerate({
        projectId,
        productImageUrl: selectedAssetUrl,
        templateImageUrl: selectedTemplateUrl,
        templateId: selectedTemplateId,
        imageMaskDataUrl,
        templateMaskDataUrl,
      });
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Anyshoot generate failed:", e);
      // eslint-disable-next-line no-alert
      alert("Failed to start anyshoot generation. Please try again.");
      setGenerating(false);
    }
  }, [
    onGenerate,
    canGenerate,
    projectId,
    selectedAssetUrl,
    selectedTemplateUrl,
    selectedTemplateId,
    buildMaskDataUrl,
    templateNatural,
    templateHasPaint,
    onClose,
  ]);

  /* ---------------- Footer ---------------- */
  const StepFooter = ({ left, right }) => (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#23233a] flex justify-between items-center px-8 py-4 z-20">
      <div>{left}</div>
      <div>{right}</div>
    </div>
  );

  const isUploadedSelected = useMemo(() => {
    if (!step1UploadedAsset || !selectedAsset) return false;
    return (
      String(selectedAsset.id || selectedAsset.assetId) ===
      String(step1UploadedAsset.id || step1UploadedAsset.assetId)
    );
  }, [selectedAsset, step1UploadedAsset]);

  /* ======================= Render ======================= */
  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center bg-black/60 px-2 py-4">
      <div className="relative bg-[#191930] w-[980px] max-w-[98vw] h-[86vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
          aria-label="Close modal"
        >
          <X size={22} />
        </button>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-8 pt-7 pb-2">
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === 1 ? "bg-pink-600 text-white" : "bg-[#29294a] text-gray-400"
            }`}
          >
            1
          </span>
          <span className="text-base sm:text-lg text-white font-semibold mr-8">
            Select Product Asset
          </span>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === 2 ? "bg-pink-600 text-white" : "bg-[#29294a] text-gray-400"
            }`}
          >
            2
          </span>
          <span className="text-base sm:text-lg text-white font-semibold mr-8">
            Select Template
          </span>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === 3 ? "bg-pink-600 text-white" : "bg-[#29294a] text-gray-400"
            }`}
          >
            3
          </span>
          <span className="text-base sm:text-lg text-white font-semibold">
            Paint Masks
          </span>
        </div>

        {/* Help block Step 1 */}
        {step === 1 && (
          <div className="mb-2 mt-2 flex items-center gap-2 px-1 text-xs bg-[#221b36] border border-pink-700 text-pink-200 rounded-lg py-2 mx-8">
            <AlertCircle className="inline mr-1 text-pink-400" size={16} />
            <span>
              <b>For best results:</b> Use a JPG or PNG photo of at least{" "}
              <b>400x400px</b>. Product must be centered, clear, with minimal
              background clutter. <b>WEBP images and videos are not supported.</b>
            </span>
          </div>
        )}

        {/* Main content (single scroll area) */}
        <div
          id="anyshoot-modal-scroll"
          className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto"
        >
          {/* Step 1: Assets (manual upload tile ALWAYS available, even when no analyzed assets) */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select a product image to use (single select).
                <span className="text-gray-400 text-sm">
                  Selected {selectedAsset ? 1 : 0} / 1
                </span>
              </p>

              {/* ✅ Manual upload input (same flow as Ad Marketing Video) */}
              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              {isAssetsInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16 select-none">
                  <Loader />
                </div>
              ) : isAssetsReloadLoading ? (
                <div className="grid grid-cols-5 gap-4">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-[#2B2B4A] bg-[#23233a] aspect-[4/5] h-[150px] md:h-[170px] animate-pulse"
                    />
                  ))}
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                    <div className="grid grid-cols-5 gap-4">
                      {/* ✅ Upload tile (same behavior as Ad Marketing Video step 1) */}
                      <div
                        className={`relative rounded-lg overflow-hidden border-2 group transition
                          ${
                            step1UploadedAsset
                              ? isUploadedSelected
                                ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                                : "border-transparent hover:border-pink-400 cursor-pointer"
                              : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                          }`}
                        onClick={() => {
                          if (step1UploadLoading) return;

                          if (step1UploadedAsset) {
                            setSelectedAsset(step1UploadedAsset);
                            return;
                          }

                          step1FileInputRef.current?.click();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (step1UploadLoading) return;

                            if (step1UploadedAsset) {
                              setSelectedAsset(step1UploadedAsset);
                              return;
                            }

                            step1FileInputRef.current?.click();
                          }
                        }}
                        aria-label={step1UploadedAsset ? "Uploaded asset" : "Upload image"}
                      >
                        <div className="aspect-[4/5] bg-black relative">
                          {step1UploadedAsset ? (
                            <img
                              className="object-cover w-full h-full"
                              src={step1UploadedAsset.url}
                              alt="Uploaded asset"
                              loading="lazy"
                              draggable={false}
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                              <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                                <Plus size={22} className="text-white" />
                              </div>
                              <div className="text-white font-semibold text-sm">
                                Upload image
                              </div>
                              <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                                JPG, PNG only
                              </div>
                            </div>
                          )}

                          {step1UploadLoading && (
                            <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                              <Loader2 className="animate-spin text-white" />
                            </div>
                          )}

                          <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                        </div>

                        {/* Actions (only when uploaded) */}
                        {step1UploadedAsset && (
                          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                            <button
                              type="button"
                              className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (step1UploadLoading) return;
                                step1FileInputRef.current?.click();
                              }}
                              aria-label="Replace uploaded image"
                              disabled={step1UploadLoading}
                            >
                              Replace
                            </button>
                            <button
                              type="button"
                              className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                const uploadedId =
                                  step1UploadedAsset.id || step1UploadedAsset.assetId;

                                setStep1UploadedAsset(null);

                                setSelectedAsset((prev) => {
                                  if (!prev) return prev;
                                  const prevId = String(prev.id || prev.assetId || "");
                                  if (prevId === String(uploadedId)) return null;
                                  return prev;
                                });
                              }}
                              aria-label="Remove uploaded image"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Existing assets */}
                      {filteredAssets.map((asset) => {
                        const url = asset.url || asset.assetUrl;
                        const isSelected =
                          !!selectedAsset &&
                          String(asset.id || asset.assetId) ===
                            String(selectedAsset.id || selectedAsset.assetId);

                        return (
                          <div
                            key={asset.id || asset.assetId}
                            className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition
                              ${
                                isSelected
                                  ? "border-pink-500 ring-2 ring-pink-600"
                                  : "border-transparent hover:border-pink-400"
                              }`}
                            onClick={() => setSelectedAsset(asset)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedAsset(asset);
                              }
                            }}
                            aria-label="Select product image"
                          >
                            <div className="aspect-[4/5] bg-black">
                              <img
                                src={assetCache.current[url] || url}
                                alt="Product"
                                className="w-full h-full object-cover"
                                loading="lazy"
                                draggable={false}
                                onLoad={() => {
                                  assetCache.current[url] = url;
                                }}
                              />
                            </div>
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {filteredAssets.length === 0 && !step1UploadedAsset && (
                    <div className="text-white/70 text-center py-6 select-none">
                      No product images found. You can still upload one using the tile above.
                    </div>
                  )}
                </>
              )}

              {assetsError && (
                <div className="text-red-400 text-center mt-2">{assetsError}</div>
              )}
            </>
          )}

          {/* Step 2: Templates */}
          {step === 2 && (
            <>
              <div className="mb-3 text-white font-medium text-lg">
                Select a template
              </div>

              <div className="mb-4 rounded-xl border border-[#2B2B4A] bg-[#1A1A31] px-4 py-3">
                <div className="text-white text-sm font-semibold">
                  Template quality: {PRODUCT_ANYSHOOT_STYLE}
                </div>
                <div className="text-white/70 text-xs mt-1">
                  This flow is currently locked to Lite to reduce TopView consumption.
                </div>
              </div>

              <div className="flex items-center mb-4 gap-3">
                <CategoryDropdown
                  options={categories}
                  value={selectedCategory}
                  onChange={(cat) => {
                    setSelectedCategory(cat);
                  }}
                  label="Category"
                />
              </div>

              {selectedTemplatePrompt ? (
                <div className="mt-2 mb-4">
                  <div className="flex items-center mb-2">
                    <p className="text-gray-300 flex-1">
                      Template prompt (optional)
                    </p>
                  </div>
                  <textarea
                    className="w-full min-h-[120px] max-h-72 bg-[#1F1F42] rounded-md p-4 text-white border border-gray-600 focus:border-pink-500 focus:ring-1 focus:ring-pink-600 transition"
                    value={selectedTemplatePrompt}
                    disabled
                    rows={6}
                  />
                </div>
              ) : null}

              <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                {isTemplatesInitialLoading ? (
                  <div className="flex items-center justify-center py-10 sm:py-16">
                    <Loader />
                  </div>
                ) : (
                  <InfiniteScroll
                    dataLength={templates.length}
                    next={fetchMoreTemplates}
                    hasMore={hasMoreTemplates}
                    loader={
                      <div className="text-white text-center py-4">
                        Loading more...
                      </div>
                    }
                    scrollableTarget="anyshoot-modal-scroll"
                    scrollThreshold={0.85}
                  >
                    <div className="grid grid-cols-5 gap-4 pb-4">
                      {templates
                        .filter(
                          (t) =>
                            !excludeTemplateIds.includes(String(t.templateId))
                        )
                        .map((t) => (
                          <TemplateCard
                            key={t.templateId}
                            template={t}
                            isSelected={
                              String(selectedTemplateId) === String(t.templateId)
                            }
                            onSelect={setSelectedTemplateId}
                            templateImageCache={templateImageCache}
                          />
                        ))}
                    </div>
                    {!templatesLoading && templates.length === 0 && (
                      <div className="text-gray-400 text-center py-10">
                        No templates found for this category.
                      </div>
                    )}
                    {templatesError && (
                      <div className="text-red-400 text-center py-5">
                        {templatesError}
                      </div>
                    )}
                  </InfiniteScroll>
                )}
              </div>
            </>
          )}

          {/* Step 3: Paint mask – TEMPLATE ONLY */}
          {step === 3 && (
            <div className="grid grid-cols-1 lg:grid-cols-[auto_280px] gap-6">
              {/* Preview + Canvas (template) */}
              <div className="relative w-full">
                <h2 className="text-xl font-bold text-white mb-3">
                  Paint mask on template
                </h2>
                <div className="relative inline-block rounded-xl bg-[#0f0f22] max-h-[620px] overflow-auto">
                  <div
                    className={`relative ${
                      cursor.visible ? "cursor-none" : "cursor-crosshair"
                    }`}
                  >
                    <img
                      ref={templateImgRef}
                      src={selectedTemplateUrl || ""}
                      alt="Template"
                      onLoad={handleTemplateImageLoaded}
                      className="block"
                    />
                    <canvas
                      ref={templateCanvasRef}
                      className="absolute top-0 left-0"
                      onMouseEnter={(e) => handleTemplatePointer(e, "enter")}
                      onMouseLeave={(e) => handleTemplatePointer(e, "leave")}
                      onMouseDown={(e) => handleTemplatePointer(e, "down")}
                      onMouseMove={(e) => handleTemplatePointer(e, "move")}
                      onMouseUp={(e) => handleTemplatePointer(e, "up")}
                      onTouchStart={(e) => handleTemplatePointer(e, "touchstart")}
                      onTouchMove={(e) => handleTemplatePointer(e, "touchmove")}
                      onTouchEnd={(e) => handleTemplatePointer(e, "touchend")}
                    />
                    {cursor.visible && <div style={ringStyle} />}
                  </div>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Paint <b>white</b> roughly where you&apos;d like the product to
                  appear in the <b>template scene</b>.
                </p>
                <button
                  type="button"
                  className="mt-3 px-3 py-1.5 rounded-xl bg-[#222246] hover:bg-[#2B2B4A] text-xs"
                  onClick={clearTemplateMask}
                >
                  Clear mask
                </button>
              </div>

              {/* Tools panel */}
              <div className="bg-[#1C1C2E] border border-[#2A2A40] rounded-2xl p-4 h-fit">
                <p className="font-semibold mb-3">Brush</p>
                <div className="flex items-center gap-3 mb-4">
                  <button
                    type="button"
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      !eraseMode
                        ? "border-pink-500 bg-[#2A2A40]"
                        : "border-transparent bg-[#1F1F2F]"
                    }`}
                    onClick={() => setEraseMode(false)}
                  >
                    <Brush size={18} /> Paint
                  </button>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <button
                    type="button"
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      eraseMode
                        ? "border-pink-500 bg-[#2A2A40]"
                        : "border-transparent bg-[#1F1F2F]"
                    }`}
                    onClick={() => setEraseMode(true)}
                  >
                    <Eraser size={18} /> Erase
                  </button>
                </div>

                <label className="text-sm text-gray-300">Brush size</label>
                <input
                  type="range"
                  min={6}
                  max={80}
                  value={brushSize}
                  onChange={(e) => setBrushSize(parseInt(e.target.value, 10))}
                  className="w-full mt-2"
                />

                <label className="text-sm text-gray-300 mt-4 block">
                  Expand mask (px)
                </label>
                <input
                  type="range"
                  min={0}
                  max={12}
                  value={expandPx}
                  onChange={(e) => setExpandPx(parseInt(e.target.value, 10))}
                  className="w-full mt-1"
                />
                <div className="text-xs text-gray-400 mt-1">
                  Helps cover edges/shadows (try 3–6).
                </div>

                <label className="text-sm text-gray-300 mt-4 block">
                  Feather (px)
                </label>
                <input
                  type="range"
                  min={0}
                  max={4}
                  value={featherPx}
                  onChange={(e) => setFeatherPx(parseInt(e.target.value, 10))}
                  className="w-full mt-1"
                />
                <div className="text-xs text-gray-400 mt-1">
                  Softens jagged strokes (1–2).
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs text-pink-200 bg-[#221b36] border border-pink-700 rounded-lg px-2 py-2">
                  <AlertCircle size={14} className="text-pink-400" />
                  <span>
                    Tip: The white area is a <b>hint</b>, not a pixel-perfect crop.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <StepFooter
          left={
            <button
              className="px-5 py-2 rounded-lg bg-[#252540] text-white hover:bg-[#363662] border border-[#353550] transition disabled:opacity-60"
              disabled={step === 1}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
            >
              Back
            </button>
          }
          right={
            step < 3 ? (
              <button
                className="px-5 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700 font-semibold transition disabled:opacity-60"
                disabled={
                  (step === 1 && !canNextFrom1) || (step === 2 && !canNextFrom2)
                }
                onClick={() => setStep((s) => s + 1)}
              >
                Next
              </button>
            ) : (
              <button
                className="px-7 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700 font-bold text-lg transition disabled:opacity-60 flex items-center justify-center"
                disabled={!canGenerate || generating}
                onClick={handleGenerate}
              >
                {generating && <Loader2 className="animate-spin mr-2" size={18} />}
                {generating ? "Generating…" : "Generate"}
              </button>
            )
          }
        />
      </div>
    </div>
  );
}
